import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Audit {
  id: string
  audit_id: string
  title: string
  audit_type: string
  department: string
  auditor: string
  audit_date: string
  status: string
  score: number
}

interface Certification {
  id: string
  certification_type: string
  name: string
  issuing_body: string
  expiry_date: string
  status: string
}

export default function SafetyPage() {
  const [audits, setAudits] = useState<Audit[]>([])
  const [certifications, setCertifications] = useState<Certification[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [audRes, certRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/safety/audits'),
      fetch(API_BASE + '/safety/certifications'),
      fetch(API_BASE + '/safety/statistics')
    ])
    if (audRes.ok) setAudits((await audRes.json()).audits || [])
    if (certRes.ok) setCertifications((await certRes.json()).certifications || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': case 'active': return 'bg-green-100 text-green-800'
      case 'scheduled': case 'pending_renewal': return 'bg-blue-100 text-blue-800'
      case 'in_progress': case 'expiring_soon': return 'bg-yellow-100 text-yellow-800'
      case 'follow_up_required': case 'expired': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 75) return 'text-blue-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Safety & Compliance</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Schedule Audit</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Audits</div>
              <div className="text-2xl font-bold">{statistics.total_audits}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Score</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.avg_audit_score}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Certs</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_certifications}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expiring Soon</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.expiring_soon}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Days Without Incident</div>
              <div className="text-2xl font-bold">{statistics.days_without_incident}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Safety Audits</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Audit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {audits.slice(0, 12).map(aud => (
                  <tr key={aud.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{aud.title}</div>
                      <div className="text-xs text-gray-500">{aud.audit_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm capitalize">{aud.audit_type} - {aud.department}</div>
                      <div className="text-xs text-gray-500">{aud.auditor} - {aud.audit_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-lg font-bold ${getScoreColor(aud.score)}`}>{aud.score}%</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(aud.status)}`}>{aud.status.replace(/_/g, ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Certifications</h3>
            <div className="space-y-3">
              {certifications.slice(0, 10).map(cert => (
                <div key={cert.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{cert.certification_type}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(cert.status)}`}>{cert.status.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-xs text-gray-500">{cert.name}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{cert.issuing_body}</span>
                    <span className="text-xs text-gray-500">Exp: {cert.expiry_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
